import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type SWRKey = string | any[] | null;
export type SWRFetcher<Data> = () => Promise<Data>;

export type SWROptions = {
  revalidateOnFocus?: boolean;
  refreshInterval?: number;
};

export type SWRResponse<Data> = {
  data?: Data;
  error?: unknown;
  isLoading: boolean;
  mutate: (
    value?: Data | ((current?: Data) => Data | undefined),
    revalidate?: boolean
  ) => Promise<Data | undefined>;
};

/**
 * Minimal SWR-like hook for environments without the swr package.
 * Supports basic stale-while-revalidate behavior, focus revalidation,
 * and manual mutation.
 */
export default function useSWR<Data = unknown>(
  key: SWRKey,
  fetcher?: SWRFetcher<Data> | null,
  options?: SWROptions
): SWRResponse<Data> {
  const [data, setData] = useState<Data>();
  const [error, setError] = useState<unknown>();
  const [isLoading, setIsLoading] = useState<boolean>(!!key && !!fetcher);
  const keyRef = useRef<SWRKey>(null);
  const dataRef = useRef<Data>();

  const serializedKey = useMemo(
    () => (key === null ? null : typeof key === "string" ? key : JSON.stringify(key)),
    [key]
  );

  const shouldFetch = key !== null && !!fetcher;

  const runFetch = useCallback(async () => {
    if (!shouldFetch || !fetcher) return dataRef.current;
    setIsLoading(true);
    try {
      const result = await fetcher();
      dataRef.current = result;
      setData(result);
      setError(undefined);
      return result;
    } catch (err) {
      setError(err);
      return undefined;
    } finally {
      setIsLoading(false);
    }
  }, [fetcher, shouldFetch]);

  useEffect(() => {
    if (serializedKey === keyRef.current) return;
    keyRef.current = serializedKey;
    if (!shouldFetch) {
      setIsLoading(false);
      return;
    }
    runFetch();
  }, [runFetch, serializedKey, shouldFetch]);

  useEffect(() => {
    if (!options?.revalidateOnFocus || !shouldFetch) return;
    const handler = () => runFetch();
    window.addEventListener("focus", handler);
    return () => window.removeEventListener("focus", handler);
  }, [options?.revalidateOnFocus, runFetch, shouldFetch]);

  useEffect(() => {
    if (!options?.refreshInterval || !shouldFetch) return;
    const id = setInterval(() => runFetch(), options.refreshInterval);
    return () => clearInterval(id);
  }, [options?.refreshInterval, runFetch, shouldFetch]);

  const mutate = useCallback(
    async (
      value?: Data | ((current?: Data) => Data | undefined),
      revalidate = true
    ) => {
      if (typeof value === "function") {
        setData((prev) => {
          const next = (value as (current?: Data) => Data | undefined)(prev);
          dataRef.current = next;
          return next;
        });
      } else if (value !== undefined) {
        dataRef.current = value;
        setData(value);
      }

      if (revalidate) {
        return runFetch();
      }

      return dataRef.current;
    },
    [runFetch]
  );

  return { data, error, isLoading, mutate };
}
